/**
 * THE PHONE DISPLAY SHEET (frame 57, phone display build PR1) — the bottom sheet the band's
 * Display pill opens: GROUP BY as numbered level rows (tap the axis name → its choices and
 * "Remove level", like the desk menu; the order pill cycles the axis's honest modes),
 * "+ Add level — up to N on this screen", SORT WITHIN GROUPS as one row (the axis name,
 * tap to change; the direction pill at the right), and the screen's FILTERS row. Every tap
 * APPLIES AT ONCE (maintainer, 3 Sep 2026: "everything should be applied the moment it is
 * picked") — no Done, no footer note; Close at the top or the back gesture leaves.
 *
 * ⚠️ NOT the desk DisplayPanel inside a Sheet: that panel is an anchored popover with
 * absolute mini-menus and a role="dialog" of its own — inside a scrolling sheet its menus
 * clip and its dialog nests. The EDITS are shared (display-edit.ts); the markup is the
 * sheet's own, low-key like the frame: hairline dividers, 28px pills, no 44px slabs.
 */
import { useState, type ReactNode } from 'react';
import { Sheet } from '../components/sheet.js';
import { displayEdit, modeLabel, orderChoices } from './display-edit.js';
import { type DisplayConfig, type EntityDisplaySpec } from './engine.js';

export function DisplaySheet<Row>({
  spec,
  config,
  onChange,
  maxLevels,
  filters,
  onClose,
}: {
  spec: EntityDisplaySpec<Row>;
  config: DisplayConfig;
  onChange: (next: DisplayConfig) => void;
  maxLevels: number;
  /** The screen's Filters row (FilterChips) — absent on screens with no filters (projects). */
  filters?: ReactNode;
  onClose: () => void;
}): JSX.Element {
  const edit = displayEdit(spec, config, onChange, maxLevels);
  /** Which row shows its choices inline: a level (by index), the add row, or the sort row. */
  const [picking, setPicking] = useState<number | 'add' | 'sort' | null>(null);

  return (
    <Sheet label="Display" onClose={onClose} className="display-sheet">
      <header className="display-sheet-head">
        <strong>Display</strong>
        <span className="filter-head-spacer" aria-hidden="true" />
        <button type="button" className="linklike display-close" onClick={onClose}>
          Close
        </button>
      </header>

      <h3 className="display-sheet-h">Group by</h3>
      {config.levels.map((level, i) => {
        const axis = edit.axisById(level.axis);
        const choices = orderChoices(axis);
        const at = choices.findIndex((c) => c.mode === level.orderMode && c.dir === level.dir);
        const nextChoice = choices[(at + 1) % choices.length];
        return (
          <div key={level.axis} className="display-row">
            <div className="display-row-main">
              <span className="display-row-n" aria-hidden="true">
                {i + 1}
              </span>
              {/* THE GROUP IS THE DOOR (maintainer, 3 Sep): tapping the name opens its
                  choices — the other axes and "Remove level" — the desk menu's own set. */}
              <button
                type="button"
                className="display-row-axis"
                aria-expanded={picking === i}
                aria-label={`Level ${String(i + 1)}: ${axis?.label ?? level.axis}`}
                onClick={() => setPicking(picking === i ? null : i)}
              >
                {axis?.label ?? level.axis}
              </button>
              <button
                type="button"
                className="display-mini"
                aria-label={`Level ${String(i + 1)} order: ${modeLabel(level.orderMode, level.dir)}`}
                disabled={choices.length < 2}
                onClick={() => {
                  if (nextChoice !== undefined) edit.setOrder(i, nextChoice.mode, nextChoice.dir);
                }}
              >
                {modeLabel(level.orderMode, level.dir)}
              </button>
            </div>
            {picking === i && (
              <div className="display-choices" role="group" aria-label={`Level ${String(i + 1)} axis`}>
                {[axis, ...edit.freeAxes]
                  .filter((a): a is NonNullable<typeof a> => a !== undefined)
                  .map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={a.id === level.axis ? 'display-mini display-mini-on' : 'display-mini'}
                      aria-pressed={a.id === level.axis}
                      onClick={() => {
                        setPicking(null);
                        if (a.id !== level.axis) edit.setAxis(i, a.id);
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                {i > 0 && (
                  <button
                    type="button"
                    className="display-mini"
                    onClick={() => {
                      setPicking(null);
                      edit.moveLevel(i, -1);
                    }}
                  >
                    Move up
                  </button>
                )}
                {i < config.levels.length - 1 && (
                  <button
                    type="button"
                    className="display-mini"
                    onClick={() => {
                      setPicking(null);
                      edit.moveLevel(i, 1);
                    }}
                  >
                    Move down
                  </button>
                )}
                <button
                  type="button"
                  className="display-mini display-mini-remove"
                  onClick={() => {
                    setPicking(null);
                    edit.removeLevel(i);
                  }}
                >
                  Remove level
                </button>
              </div>
            )}
          </div>
        );
      })}
      {edit.canAdd && (
        <div className="display-row">
          <div className="display-row-main display-row-add">
            <button
              type="button"
              className="linklike display-add"
              aria-expanded={picking === 'add'}
              onClick={() => setPicking(picking === 'add' ? null : 'add')}
            >
              + Level
            </button>
            <span className="display-note">up to {maxLevels} on this screen</span>
          </div>
          {picking === 'add' && (
            <div className="display-choices" role="group" aria-label="Add level">
              {edit.freeAxes.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="display-mini"
                  onClick={() => {
                    setPicking(null);
                    edit.addLevel(a.id);
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {config.levels.length === 0 && !edit.canAdd && (
        <div className="display-row">
          <div className="display-row-main">
            <span className="display-note">Nothing to group by on this screen.</span>
          </div>
        </div>
      )}

      <h3 className="display-sheet-h">Sort within groups</h3>
      <div className="display-row">
        <div className="display-row-main">
          {/* "None" until somebody picks (engine.ts `sortChosen`), and no direction pill to flip
              until there is a chosen axis to flip — the same words the desk chip shows. */}
          <button
            type="button"
            className="display-row-axis"
            aria-expanded={picking === 'sort'}
            aria-label={`Sort: ${config.sortChosen === true ? (edit.sortAxis?.label ?? config.sortAxis) : 'None'}`}
            onClick={() => setPicking(picking === 'sort' ? null : 'sort')}
          >
            {config.sortChosen === true ? (edit.sortAxis?.label ?? config.sortAxis) : 'None'}
          </button>
          {config.sortChosen === true && (
            <button
              type="button"
              className="display-mini display-mini-on"
              aria-label={`Direction: ${config.sortDir === 'asc' ? 'ascending' : 'descending'}`}
              onClick={() => edit.setSort(config.sortAxis, config.sortDir === 'asc' ? 'desc' : 'asc')}
            >
              {config.sortDir === 'asc' ? 'Ascending' : 'Descending'}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                {config.sortDir === 'asc' ? (
                  <path d="M6 9.5 L6 2.5 M3 5.5 L6 2.5 L9 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <path d="M6 2.5 L6 9.5 M3 6.5 L6 9.5 L9 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </button>
          )}
        </div>
        {picking === 'sort' && (
          <div className="display-choices" role="group" aria-label="Sort axis">
            {spec.sortAxes.map((a) => (
              <button
                key={a.id}
                type="button"
                className={config.sortChosen === true && config.sortAxis === a.id ? 'display-mini display-mini-on' : 'display-mini'}
                aria-pressed={config.sortChosen === true && config.sortAxis === a.id}
                onClick={() => {
                  setPicking(null);
                  edit.setSort(a.id, config.sortDir);
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {filters !== undefined && (
        <>
          <h3 className="display-sheet-h">Filters</h3>
          <div className="display-row">
            <div className="display-row-main">{filters}</div>
          </div>
        </>
      )}
    </Sheet>
  );
}
